// Item 70 Phase F — admin observability snapshot.
//
// Read-only point-in-time view of the most-watched RED + USE metrics
// from the API server's Prometheus registry. Backs the
// /admin/observability page with a 10-second auto-refresh so operators
// can watch a live triage view without leaving the AdminShell.

import useSWR from 'swr';

export interface ObservabilityBuild {
  version: string;
  commit: string;
  built_at?: string;
  go_version: string;
}

export interface ObservabilityHTTP {
  requests_total: number;
  requests_5xx_total: number;
  requests_4xx_total: number;
  inflight: number;
  duration_count: number;
  duration_sum_seconds: number;
}

export interface ObservabilityPgPool {
  acquired: number;
  idle: number;
  total: number;
  max: number;
  saturation: number;
}

export interface ObservabilityS3 {
  requests_total: number;
  errors_total: number;
  duration_count: number;
  duration_sum_seconds: number;
}

export interface ObservabilityScan {
  queue_pending: number;
  queue_claimed: number;
  queue_running: number;
  queue_failed: number;
  processed_total: number;
  failed_total: number;
}

export interface ObservabilityRetention {
  sweep_duration_count: number;
  sweep_duration_sum_seconds: number;
  artifacts_deleted_total: number;
}

export interface ObservabilityReplicationPeer {
  peer: string;
  lag_seconds: number;
  outbox_depth: number;
  push_errors_total: number;
}

export interface ObservabilityReplication {
  peers: ObservabilityReplicationPeer[];
}

export interface ObservabilityLicense {
  checks_total: number;
  expires_in_seconds: number;
}

export interface ObservabilitySnapshot {
  generated_at: string;
  uptime_seconds: number;
  build: ObservabilityBuild;
  http: ObservabilityHTTP;
  pg_pool: ObservabilityPgPool;
  s3: ObservabilityS3;
  scan: ObservabilityScan;
  retention: ObservabilityRetention;
  replication: ObservabilityReplication;
  license: ObservabilityLicense;
}

async function fetcher(url: string): Promise<ObservabilitySnapshot> {
  const res = await fetch(url, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

// 10s refresh matches the Grafana-default scrape interval and the
// pgxpool poller cadence in the backend, so values on the page line up
// with what an operator's scrape sees.
export function useObservabilitySnapshot() {
  return useSWR<ObservabilitySnapshot>(
    '/api/admin/observability/snapshot',
    fetcher,
    {
      refreshInterval: 10_000,
      revalidateOnFocus: true,
      shouldRetryOnError: false,
    },
  );
}
